/**
 * OTP / 2FA Verification Code detector for incoming emails.
 * Detects patterns like:
 * - "código de verificación es: 123456"
 * - "verification code: 849201"
 * - "tu código es 492019"
 * - "one-time password: 938210"
 * - "tu clave dinámica es: 123-456"
 */

export interface DetectedOtpCode {
  code: string;
  label?: string;
}

export function detectVerificationCode(text: string, subject?: string): DetectedOtpCode | null {
  if (!text && !subject) return null;

  const combined = `${subject || ""}\n${text || ""}`;

  // 1. Direct contextual regex patterns (Spanish & English bank/auth patterns)
  const patterns = [
    /(?:código(?: de verificación| de seguridad| temporal| único| dinámico)?|tu código|clave dinámica|código OTP|OTP|verification code|security code|one-time password|authorization code|passcode|secret code)[\s:=—–-]+([A-Z0-9]{4,8}|\d{3}[-\s]\d{3})/i,
    /(?:usa el código|use code|enter code|ingresa el código|código para continuar)[\s:=—–-]+([A-Z0-9]{4,8}|\d{3}[-\s]\d{3})/i,
    /(?:code is|código es)[\s:=—–-]+([A-Z0-9]{4,8}|\d{3}[-\s]\d{3})/i,
    /\b([0-9]{3}[-\s][0-9]{3})\b/, // 123-456 or 123 456
  ];

  for (const regex of patterns) {
    const match = combined.match(regex);
    if (match && match[1]) {
      const rawCode = match[1].replace(/[-\s]/g, "").trim();
      // Ensure it's not a year (like 2024, 2025, 2026) or a generic small number
      if (/^\d{4,8}$/.test(rawCode)) {
        const num = parseInt(rawCode, 10);
        if (rawCode.length === 4 && num >= 1990 && num <= 2035) {
          continue; // Probable year
        }
        return { code: rawCode };
      }
      if (/^[A-Z0-9]{5,8}$/.test(rawCode)) {
        return { code: rawCode };
      }
    }
  }

  // 2. Standalone 6-digit numeric token highlighted or in quotes
  const quoteMatch = combined.match(/["'«»“]([0-9]{4,8})["'«»”]/);
  if (quoteMatch && quoteMatch[1]) {
    const raw = quoteMatch[1];
    const num = parseInt(raw, 10);
    if (!(raw.length === 4 && num >= 1990 && num <= 2035)) {
      return { code: raw };
    }
  }

  return null;
}
